import { useState } from 'react'
import { redirectToImage } from '@/utils/redirect'
import { getRandomPrompt } from '@/config/example-prompts'

const EXAMPLE_VIDEOS = [
  'https://funnyclips-content.s3.us-east-1.amazonaws.com/example14.mp4',
  'https://funnyclips-content.s3.us-east-1.amazonaws.com/example15.mp4',
  'https://funnyclips-content.s3.us-east-1.amazonaws.com/example_4.mp4',
  'https://funnyclips-content.s3.us-east-1.amazonaws.com/example_8.mp4',
]

export function SqueezeFreeformPage() {
  const [prompt, setPrompt] = useState('')

  const handleCreate = () => {
    if (!prompt.trim()) return
    redirectToImage('t2i', { prompt: prompt.trim() })
  }

  const handleGiveMeAnIdea = () => {
    setPrompt(getRandomPrompt(prompt))
  }

  return (
    <div className="h-dvh overflow-y-auto bg-black text-white">
      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center px-4 py-12">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900 opacity-50" />

        <div className="relative z-10 max-w-7xl mx-auto w-full">
          {/* Badge */}
          <div className="text-center lg:text-left mb-8">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/20 rounded-full px-4 py-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              <span className="text-white text-sm font-semibold">
                No Limits. No Rules. No Filter.
              </span>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            {/* Left Side - Prompt Input */}
            <div className="flex-1 text-center lg:text-left space-y-8 w-full">
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-black leading-tight">
                Dream Up Your
                <br />
                Dream Girl
              </h1>

              <p className="text-xl md:text-2xl text-gray-300">
                Describe her and generate a video of her doing anything you want
              </p>

              {/* Prompt Input */}
              <div className="space-y-4 max-w-xl mx-auto lg:mx-0">
                <div className="relative">
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe your dream girl... e.g. a beautiful brunette with green eyes, wearing a red dress, dancing on the beach at sunset"
                    rows={4}
                    className="w-full bg-white/5 border border-white/20 rounded-2xl px-5 py-4 pb-14 text-white placeholder-gray-500 text-lg resize-none focus:outline-none focus:border-white/40 focus:ring-1 focus:ring-white/20 transition-all"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleCreate()
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleGiveMeAnIdea}
                    className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 text-white text-sm font-semibold px-3 py-1.5 rounded-full transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                    Give me an idea
                  </button>
                </div>

                <button
                  onClick={handleCreate}
                  disabled={!prompt.trim()}
                  className="group relative inline-flex items-center gap-3 bg-white hover:bg-gray-200 text-black text-xl md:text-2xl font-bold px-10 py-5 md:px-12 md:py-6 rounded-full transition-all duration-300 shadow-[0_0_30px_rgba(255,255,255,0.3)] hover:shadow-[0_0_50px_rgba(255,255,255,0.5)] hover:scale-105 disabled:opacity-40 disabled:hover:scale-100 disabled:hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] disabled:cursor-not-allowed w-full justify-center lg:w-auto"
                >
                  Create Her Now
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Right Side - Example Videos */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {EXAMPLE_VIDEOS.map((src) => (
                <div
                  key={src}
                  className="rounded-2xl overflow-hidden border-2 border-white/20 shadow-[0_0_20px_rgba(255,255,255,0.1)]"
                >
                  <video
                    src={src}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-32 sm:w-40 lg:w-44 object-cover"
                    style={{ aspectRatio: '9/16' }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Trust Section */}
      <div className="relative px-4 py-16 bg-black">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-3">
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white">Lightning Fast</h3>
              <p className="text-gray-400">Your image is ready in seconds</p>
            </div>

            <div className="text-center space-y-3">
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white">100% Private</h3>
              <p className="text-gray-400">Your images and videos are never shared</p>
            </div>

            <div className="text-center space-y-3">
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white">No Censorship</h3>
              <p className="text-gray-400">Create exactly what you imagine</p>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="relative px-4 py-8 bg-black border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-500 text-sm leading-relaxed">
            The "No Filter" mode is designed for creative and entertainment
            purposes. Users are responsible for ensuring their content complies
            with platform guidelines and applicable laws. PixelMaker does not
            endorse or take responsibility for user-generated content.
          </p>
        </div>
      </div>
    </div>
  )
}
